import { Helmet } from 'react-helmet-async';
import keywords from '../data/keywords.json';

interface SEOProps {
    title: string;
    description: string;
    path: string;
    city?: string;
    service?: string;
}

export const SEO: React.FC<SEOProps> = ({ 
  title,
  description,
  path,
  city,
  service
}) => {
  const baseUrl = 'https://valutaauto24.it';
  const currentUrl = `${baseUrl}${path}`;
  
  // For home page, use just the title without the site name
  const isHomePage = path === '/';
  const pageTitle = isHomePage ? title : `${title}`;
  
  const pageDescription = description || 
    `Servizio professionale di ${service?.toLowerCase()} ${city ? `a ${city}` : ''}. Offriamo valutazioni auto accurate e affidabili con il miglior prezzo di mercato.`;

  // Generate localized keywords based on service and city
  const localizedKeywords = city ? [
    ...keywords.map(keyword => `${keyword} ${city}`),
    ...keywords
  ] : keywords;

  // Generate structured data for service pages
  const generateStructuredData = () => {
    if (!service || !city) return null;

    return {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "ValutaAuto",
      "description": pageDescription,
      "url": baseUrl,
      "areaServed": {
        "@type": "City",
        "name": city,
        "@id": `https://it.wikipedia.org/wiki/${city.replace(/\s+/g, '_')}`
      },
      "serviceType": service,
      "priceRange": "Gratuito",
      "telephone": "+39-351-234-5678",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "IT",
        "addressRegion": "Italia"
      },
      "geo": {
        "@type": "GeoCoordinates"  
      },
      "openingHours": "Mo-Fr 09:00-18:00",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "EUR",
        "description": "Valutazione auto gratuita"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "250"
      }
    };
  };
  
  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={localizedKeywords.join(', ')} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={`${baseUrl}/img/og-image.jpg`} />
      <meta property="og:site_name" content="ValutaAuto" />
      <meta property="og:locale" content="it_IT" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={`${baseUrl}/img/og-image.jpg`} />
      <meta name="twitter:site" content="@ValutaAuto" />

      {/* Additional SEO tags */}
      <meta name="robots" content="index, follow" />
      <meta name="language" content="it-IT" />
      <meta name="author" content="ValutaAuto" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="theme-color" content="#fdc751" />
      
      {/* Geographic tags */}
      {city && (
        <>
          <meta name="geo.region" content={`IT-${city}`} />
          <meta name="geo.placename" content={city} />
          <meta name="ICBM" content="" />
        </>
      )}
      
      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />
      
      {/* Preconnect for performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />

      {/* Structured Data */}
      {generateStructuredData() && (
        <script type="application/ld+json">
          {JSON.stringify(generateStructuredData())}
        </script>
      )}
    </Helmet>
  );
};